import { Children } from "react";
import SectionHead from "@/components/yts/SectionHead";
import RailArrows from "@/components/yts/RailArrows";
import { Section } from "@/components/yts/HomeSections";
import { useRailScroll } from "@/lib/useRailScroll";

const DEFAULT_ITEM_WIDTH = "w-[min(69vw,256px)] min-w-[190px] sm:w-[258px] sm:min-w-0";

/** One visual treatment and predictable card spacing on every carousel. */
export default function CarouselRail({ title, subtitle = null, action = null, itemWidth = DEFAULT_ITEM_WIDTH, editorial = false, masthead = null, children }) {
  const { ref, nudge, canPrev, canNext } = useRailScroll();
  const items = Children.toArray(children);
  if (!items.length) return null;

  const accessibleName = typeof title === "string" ? title : (masthead || "Explore Puerto Rico");
  const arrows = items.length > 1
    ? <RailArrows onPrev={() => nudge(-1)} onNext={() => nudge(1)} canPrev={canPrev} canNext={canNext} />
    : null;

  return (
    <Section className={editorial ? "editorial-rail editorial-rail-feature" : "editorial-rail"}>
      {masthead && (
        <p className="mb-2 flex items-center gap-2 border-t border-current/15 pt-3 text-[9px] font-bold uppercase tracking-[.13em] opacity-65">
          {masthead}
        </p>
      )}
      <SectionHead
        title={title}
        subtitle={subtitle}
        action={<div className="flex shrink-0 items-center gap-1.5">{action}{arrows}</div>}
      />
      <div
        ref={ref}
        role="region"
        tabIndex={0}
        aria-roledescription="carousel"
        aria-label={accessibleName}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            nudge(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
        className="no-scrollbar flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto overscroll-x-contain pb-2 scroll-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ce3830] sm:gap-4"
      >
        {items.map((child, i) => (
          <div key={i} className={`${itemWidth} shrink-0 snap-start [&>*]:w-full`}>{child}</div>
        ))}
      </div>
    </Section>
  );
}
